import { CashBackPolicy } from '@fruitful/types';
import { Link } from 'expo-router';
import * as React from 'react';
import { useState } from 'react';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallEmphasized,
    Button,
    ButtonSize,
    ButtonVariant,
    CheckCircleIcon,
    H2Emphasized,
    Icon,
    isNonMobileWeb,
    Sheet,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { useAuthedUser } from '../../../../common/providers/AuthedUserProvider';
import { toCardFriendlyName } from '../../../../common/utils/cardUtils';
import { formatFullName } from '../../../../common/utils/userUtils';
import { CardForm } from '../../../types/Card';
import { DummyFruitfulCard } from '../../cards/FruitfulCard';
import {
    FruitfulCardDisclosure,
    POLICY_DISCLOSURES,
    PolicyType,
} from '../../Disclosures';
import { CardPreferenceSelector, RequestedCardType } from './CardPreference';

const InfoRow = ({ text }: { text: string | React.ReactNode }) => {
    return (
        <XStack gap={12}>
            <View width={20}>
                <Icon icon={CheckCircleIcon} color="$gray1100" size={20} />
            </View>
            <View flex={1}>
                <BodyMediumRegular>{text}</BodyMediumRegular>
            </View>
        </XStack>
    );
};

const AddFruitfulCard = ({
    onPressAddCard,
    onPressSkip,
    name,
    hasExistingPhysicalCard,
}: {
    onPressAddCard: (cardType: RequestedCardType) => void;
    onPressSkip: () => void;
    name: string[];
    hasExistingPhysicalCard: boolean;
}) => {
    const [showCardSelector, setShowCardSelector] = useState<boolean>(false);
    const [firstName, lastName] = name;

    const user = useAuthedUser();
    const cashBackRate =
        user.membership?.product?.banking?.rateCashBack ?? '2%';
    const cashBackPolicy = user.membership?.product?.banking?.cashBackPolicy;

    return (
        <>
            <YStack
                alignItems="center"
                paddingTop={isNonMobileWeb ? '10vh' : 0}
            >
                <YStack alignSelf="center" marginTop={8} marginBottom={24}>
                    <DummyFruitfulCard
                        form={CardForm.PHYSICAL}
                        rotate="-2deg"
                        showFormLabel={false}
                        cardHolderName={toCardFriendlyName(
                            formatFullName(firstName, lastName),
                        )}
                    />
                </YStack>
                <H2Emphasized textAlign="center" marginBottom={24}>
                    Score rewards while you spend responsibly
                </H2Emphasized>
                <YStack gap={12} width="100%" $sm={{ width: '82%' }}>
                    <InfoRow
                        text={
                            <>
                                <BodyMediumEmphasized>
                                    {cashBackRate} cash back*
                                </BodyMediumEmphasized>{' '}
                                {cashBackPolicy ===
                                CashBackPolicy.DD_REQUIREMENT
                                    ? 'on all your spending with a qualifying direct deposit.'
                                    : 'on all your spending.'}
                            </>
                        }
                    />
                    <InfoRow text="Linked to your Fruitful Cash Account, so you never spend more than you have." />
                    <InfoRow text="No interest, no carry-over balances." />
                </YStack>
                <YStack marginTop={32} width="90%" maxWidth={520}>
                    <Link
                        href={
                            POLICY_DISCLOSURES[PolicyType.CARD_REWARDS_TERMS]
                                .url
                        }
                        target="_blank"
                    >
                        <XStack
                            justifyContent="center"
                            backgroundColor="$gray100"
                            borderRadius={24}
                            paddingVertical={8}
                            $sm={{ paddingHorizontal: 8 }}
                        >
                            <BodySmallEmphasized
                                color="$gray1100"
                                textAlign="center"
                                width="100%"
                                $mobile_mini={{ fontSize: 14 }}
                                $sm={{ whiteSpace: 'nowrap' }}
                            >
                                *See Fruitful Card Rewards Program{'\n'}
                                Terms and Conditions
                            </BodySmallEmphasized>
                        </XStack>
                    </Link>
                </YStack>
            </YStack>
            <YStack gap={8}>
                <YStack gap={16}>
                    <FruitfulCardDisclosure />
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        text="Add a Fruitful Card"
                        onPress={() => {
                            if (hasExistingPhysicalCard) {
                                onPressAddCard(RequestedCardType.VIRTUAL_ONLY);
                            } else {
                                setShowCardSelector(true);
                            }
                        }}
                    />
                </YStack>
                <Button
                    variant={ButtonVariant.TERTIARY}
                    size={ButtonSize.LARGE}
                    text="Maybe later"
                    onPress={onPressSkip}
                />
            </YStack>
            <Sheet
                isOpen={showCardSelector}
                onClose={() => {
                    setShowCardSelector(false);
                }}
                dismissOnOverlayPress={true}
                showCloseButton={false}
            >
                <CardPreferenceSelector
                    selectVirtual={(_selection) => {
                        setShowCardSelector(false);
                        onPressAddCard(RequestedCardType.VIRTUAL_ONLY);
                    }}
                    selectPhysical={(_selection) => {
                        setShowCardSelector(false);
                        onPressAddCard(RequestedCardType.VIRTUAL_AND_PHYSICAL);
                    }}
                    hasExistingPhysicalCards={hasExistingPhysicalCard}
                />
            </Sheet>
        </>
    );
};

export default AddFruitfulCard;
